import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

export default function Register() {
  const { API, loginWithToken } = useAuth();
  const navigate                = useNavigate();

  const [step, setStep]       = useState('form'); // form | success | pending
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('pending') === 'true') {
      setStep('success');
    }
  }, []);

  const [form, setForm] = useState({
    fullName:      '',
    companyName:   '',
    email:         '',
    phone:         '',
    whatsAppNumber: '',
    password:      '',
    confirmPassword: '',
  });

  const handleRegister = async () => {
    setError('');

    if (!form.fullName || !form.email ||
        !form.password || !form.confirmPassword) {
      setError('Please fill in all required fields');
      return;
    }

    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (form.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      await axios.post(API + '/users/register', {
        fullName:      form.fullName,
        companyName:   form.companyName,
        email:         form.email.toLowerCase(),
        phone:         form.phone,
        whatsAppNumber: form.whatsAppNumber,
        password:      form.password,
      });
      setStep('success');
    } catch (e) {
      setError(e.response?.data || 'Registration failed. Please try again.');
    }
    setLoading(false);
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(API + '/users/google-login', {
        googleToken: credentialResponse.credential,
      });

      if (res.data.success) {
        loginWithToken(res.data);
        navigate('/dashboard');
      } else {
        setStep('pending');
      }
    } catch (e) {
      setError('Google login failed. Please try again.');
    }
    setLoading(false);
  };

  // ── SUCCESS PAGE ──
  if (step === 'success' || step === 'pending') {
    return (
      <div style={styles.page}>
        <div style={styles.grid} />
        <div style={styles.card}>
          <div style={styles.successIcon}>✓</div>
          <h2 style={styles.successTitle}>Registration Successful!</h2>
          <p style={styles.successText}>
            Your account has been created successfully.
            Our team will review and activate your account shortly.
            You will receive an email once your account is approved.
          </p>
          <button
            onClick={() => navigate('/')}
            style={styles.btn}>
            BACK TO LOGIN
          </button>
        </div>
      </div>
    );
  }

  // ── REGISTER FORM ──
  return (
    <div style={styles.page}>
      <div style={styles.grid} />

      <div style={styles.card}>

        {/* Logo */}
        <div style={styles.logoWrap}>
          <div style={styles.logoIcon}>FX</div>
          <div>
            <div style={styles.logoTitle}>FX CAPITAL INDIA</div>
            <div style={styles.logoSub}>Create Your Account</div>
          </div>
        </div>

        <div style={styles.divider} />

        {/* Google Login */}
        <div style={styles.googleWrap}>
          <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={() => setError('Google login failed')}
            text="signup_with"
            shape="rectangular"
            theme="filled_black"
            width="100%"
          />
        </div>

        <div style={styles.orRow}>
          <div style={styles.orLine} />
          <span style={styles.orText}>OR</span>
          <div style={styles.orLine} />
        </div>

        {/* Form */}
        <div style={styles.form}>

          <div style={styles.formGrid}>

            {/* Full Name */}
            <div style={styles.fieldWrap}>
              <label style={styles.label}>FULL NAME *</label>
              <input
                style={styles.input}
                value={form.fullName}
                onChange={e => setForm({...form, fullName: e.target.value})}
                placeholder="Your full name"
                onFocus={e => e.target.style.borderColor = '#C9A84C'}
                onBlur={e  => e.target.style.borderColor = '#1E3050'}
              />
            </div>

            {/* Company Name */}
            <div style={styles.fieldWrap}>
              <label style={styles.label}>COMPANY NAME</label>
              <input
                style={styles.input}
                value={form.companyName}
                onChange={e => setForm({...form, companyName: e.target.value})}
                placeholder="Your company name"
                onFocus={e => e.target.style.borderColor = '#C9A84C'}
                onBlur={e  => e.target.style.borderColor = '#1E3050'}
              />
            </div>

            {/* Email */}
            <div style={{ ...styles.fieldWrap, gridColumn: '1 / -1' }}>
              <label style={styles.label}>EMAIL ADDRESS *</label>
              <input
                type="email"
                style={styles.input}
                value={form.email}
                onChange={e => setForm({...form, email: e.target.value})}
                placeholder="your@email.com"
                onFocus={e => e.target.style.borderColor = '#C9A84C'}
                onBlur={e  => e.target.style.borderColor = '#1E3050'}
              />
            </div>

            {/* Phone */}
            <div style={styles.fieldWrap}>
              <label style={styles.label}>PHONE NUMBER</label>
              <input
                style={styles.input}
                value={form.phone}
                onChange={e => setForm({...form, phone: e.target.value})}
                placeholder="Mobile number"
                onFocus={e => e.target.style.borderColor = '#C9A84C'}
                onBlur={e  => e.target.style.borderColor = '#1E3050'}
              />
            </div>

            {/* WhatsApp */}
            <div style={styles.fieldWrap}>
              <label style={styles.label}>WHATSAPP NUMBER</label>
              <input
                style={styles.input}
                value={form.whatsAppNumber}
                onChange={e => setForm({...form, whatsAppNumber: e.target.value})}
                placeholder="WhatsApp number"
                onFocus={e => e.target.style.borderColor = '#C9A84C'}
                onBlur={e  => e.target.style.borderColor = '#1E3050'}
              />
            </div>

            {/* Password */}
            <div style={styles.fieldWrap}>
              <label style={styles.label}>PASSWORD *</label>
              <input
                type="password"
                autoComplete="new-password"
                style={styles.input}
                value={form.password}
                onChange={e => setForm({...form, password: e.target.value})}
                placeholder="Minimum 6 characters"
                onFocus={e => e.target.style.borderColor = '#C9A84C'}
                onBlur={e  => e.target.style.borderColor = '#1E3050'}
              />
            </div>

            {/* Confirm Password */}
            <div style={styles.fieldWrap}>
              <label style={styles.label}>CONFIRM PASSWORD *</label>
              <input
                type="password"
                style={styles.input}
                value={form.confirmPassword}
                onChange={e => setForm({...form, confirmPassword: e.target.value})}
                placeholder="Repeat password"
                onFocus={e => e.target.style.borderColor = '#C9A84C'}
                onBlur={e  => e.target.style.borderColor = '#1E3050'}
              />
            </div>

          </div>

          {error && (
            <div style={styles.error}>{error}</div>
          )}

          <button
            onClick={handleRegister}
            disabled={loading}
            style={{
              ...styles.btn,
              opacity: loading ? 0.7 : 1,
              cursor:  loading ? 'not-allowed' : 'pointer',
              marginTop: '8px',
            }}>
            {loading ? 'REGISTERING...' : 'CREATE ACCOUNT'}
          </button>

          <div style={styles.loginRow}>
            Already have an account?{' '}
            <span
              onClick={() => navigate('/')}
              style={styles.loginLink}>
              Sign In
            </span>
          </div>

        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight:      '100vh',
    background:     '#080F1A',
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    position:       'relative',
    overflow:       'hidden',
    padding:        '24px',
  },
  grid: {
    position:   'absolute',
    inset:      0,
    background: `
      linear-gradient(rgba(37,99,168,0.04) 1px, transparent 1px),
      linear-gradient(90deg, rgba(37,99,168,0.04) 1px, transparent 1px)
    `,
    backgroundSize: '40px 40px',
    pointerEvents:  'none',
  },
  card: {
    background:   '#0D1B2A',
    border:       '1px solid #1E3050',
    borderRadius: '12px',
    padding:      '40px 44px',
    width:        '100%',
    maxWidth:     '560px',
    position:     'relative',
    boxShadow:    '0 24px 64px rgba(0,0,0,0.5)',
  },
  logoWrap: {
    display:      'flex',
    alignItems:   'center',
    gap:          '14px',
    marginBottom: '24px',
  },
  logoIcon: {
    width:          '48px',
    height:         '48px',
    background:     'linear-gradient(135deg, #C9A84C, #8B6914)',
    borderRadius:   '10px',
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    fontFamily:     'Rajdhani, sans-serif',
    fontSize:       '20px',
    fontWeight:     700,
    color:          '#080F1A',
  },
  logoTitle: {
    fontFamily:    'Rajdhani, sans-serif',
    fontSize:      '18px',
    fontWeight:    700,
    color:         '#C9A84C',
    letterSpacing: '0.1em',
  },
  logoSub: {
    fontFamily: 'Inter, sans-serif',
    fontSize:   '11px',
    color:      '#8896A8',
    marginTop:  '2px',
  },
  divider: {
    height:       '1px',
    background:   'linear-gradient(90deg, transparent, #1E3050, transparent)',
    marginBottom: '24px',
  },
  googleWrap: {
    display:        'flex',
    justifyContent: 'center',
    marginBottom:   '20px',
  },
  orRow: {
    display:     'flex',
    alignItems:  'center',
    gap:         '12px',
    marginBottom: '20px',
  },
  orLine: {
    flex:       1,
    height:     '1px',
    background: '#1E3050',
  },
  orText: {
    fontFamily: 'Inter, sans-serif',
    fontSize:   '12px',
    color:      '#4A5568',
  },
  form: {
    display:       'flex',
    flexDirection: 'column',
    gap:           '16px',
  },
  formGrid: {
    display:             'grid',
    gridTemplateColumns: '1fr 1fr',
    gap:                 '14px',
  },
  fieldWrap: {
    display:       'flex',
    flexDirection: 'column',
    gap:           '6px',
  },
  label: {
    fontFamily:    'Rajdhani, sans-serif',
    fontSize:      '11px',
    fontWeight:    600,
    color:         '#8896A8',
    letterSpacing: '0.12em',
  },
  input: {
    background:   '#111E2E',
    border:       '1px solid #1E3050',
    borderRadius: '6px',
    padding:      '10px 14px',
    color:        '#E8EDF5',
    fontSize:     '13px',
    fontFamily:   'Inter, sans-serif',
    outline:      'none',
    transition:   'border-color 0.2s',
    width:        '100%',
  },
  error: {
    background:   'rgba(239,68,68,0.08)',
    border:       '1px solid rgba(239,68,68,0.3)',
    borderRadius: '6px',
    padding:      '10px 14px',
    color:        '#EF4444',
    fontSize:     '13px',
    fontFamily:   'Inter, sans-serif',
  },
  btn: {
    background:    'linear-gradient(135deg, #C9A84C, #8B6914)',
    border:        'none',
    borderRadius:  '6px',
    padding:       '12px',
    color:         '#080F1A',
    fontFamily:    'Rajdhani, sans-serif',
    fontSize:      '15px',
    fontWeight:    700,
    letterSpacing: '0.1em',
    width:         '100%',
  },
  loginRow: {
    textAlign:  'center',
    fontSize:   '13px',
    color:      '#8896A8',
    fontFamily: 'Inter, sans-serif',
  },
  loginLink: {
    color:  '#C9A84C',
    cursor: 'pointer',
  },
  successIcon: {
    width:          '64px',
    height:         '64px',
    background:     'rgba(34,197,94,0.1)',
    border:         '2px solid rgba(34,197,94,0.3)',
    borderRadius:   '50%',
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    fontSize:       '28px',
    color:          '#22C55E',
    margin:         '0 auto 24px',
  },
  successTitle: {
    fontFamily:    'Rajdhani, sans-serif',
    fontSize:      '22px',
    fontWeight:    700,
    color:         '#C9A84C',
    textAlign:     'center',
    marginBottom:  '16px',
  },
  successText: {
    fontFamily:   'Inter, sans-serif',
    fontSize:     '14px',
    color:        '#8896A8',
    textAlign:    'center',
    lineHeight:   1.6,
    marginBottom: '24px',
  },
};